import { Header } from '@/components/Header';
import { Hero } from '@/components/Hero';
import { TrustBar } from '@/components/TrustBar';
import { AnimatedSection } from '@/components/AnimatedSection';
import { DeferredSection } from '@/components/DeferredSection';
import { SEOHead } from '@/components/SEOHead';
import { Analytics } from '@/components/Analytics';
import { FacebookPixel } from '@/components/FacebookPixel';

import { useLanguage } from '@/i18n/LanguageContext';
import { lazy, Suspense } from 'react';
import { SITE_ORIGIN } from '@/lib/siteOrigin';

// Lazy-load all below-fold sections to reduce initial JS
const HomeTrustProof = lazy(() => import('@/components/HomeTrustProof').then(m => ({ default: m.HomeTrustProof })));
const AvailabilityProofPanel = lazy(() => import('@/components/AvailabilityProofPanel').then(m => ({ default: m.AvailabilityProofPanel })));
const WhyConsultic = lazy(() => import('@/components/WhyConsultic').then(m => ({ default: m.WhyConsultic })));
const SiteExplorer = lazy(() => import('@/components/SiteExplorer').then(m => ({ default: m.SiteExplorer })));
const Services = lazy(() => import('@/components/Services').then(m => ({ default: m.Services })));
const About = lazy(() => import('@/components/About').then(m => ({ default: m.About })));
const Testimonials = lazy(() => import('@/components/Testimonials').then(m => ({ default: m.Testimonials })));
const GoogleReviews = lazy(() => import('@/components/GoogleReviews').then(m => ({ default: m.GoogleReviews })));
const ClientsMarquee = lazy(() => import('@/components/ClientsMarquee').then(m => ({ default: m.ClientsMarquee })));
const News = lazy(() => import('@/components/News').then(m => ({ default: m.News })));
const QuoteForm = lazy(() => import('@/components/QuoteForm').then(m => ({ default: m.QuoteForm })));
const Contact = lazy(() => import('@/components/Contact').then(m => ({ default: m.Contact })));
// const Recruitment = lazy(() => import('@/components/Recruitment').then(m => ({ default: m.Recruitment }))); // Removed from homepage
const Footer = lazy(() => import('@/components/Footer').then(m => ({ default: m.Footer })));

// Invisible fallback — no CLS
const Blank = () => <div />;

const Index = () => {
  const { t } = useLanguage();

  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "ConsulTIC",
    "alternateName": "ConsulTIC IT Solutions",
    "url": SITE_ORIGIN,
    "logo": `${SITE_ORIGIN}/images/logo-email-horizontal.png`,
    "description": t.footer.description,
    "foundingDate": "2012",
    "numberOfEmployees": {
      "@type": "QuantitativeValue",
      "minValue": 18,
      "maxValue": 25
    },
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "Boulevard Millenium 11",
      "addressLocality": "Houdeng-Goegnies",
      "postalCode": "7110",
      "addressCountry": "BE"
    },
    "contactPoint": [
      {
        "@type": "ContactPoint",
        "telephone": "+32-2-880-70-70",
        "contactType": "customer service",
        "areaServed": ["BE", "LU"],
        "availableLanguage": ["French", "Dutch", "English"]
      }
    ],
    "email": "contact@consultic.com",
    "sameAs": [
      "https://www.linkedin.com/company/consultic",
      "https://www.facebook.com/consultic"
    ],
    "areaServed": [
      { "@type": "Country", "name": "Belgium" },
      { "@type": "Country", "name": "Luxembourg" }
    ],
    "knowsAbout": [
      "IT Infrastructure",
      "Cybersecurity",
      "Cloud Computing",
      "IT Support",
      "Managed IT Services",
      "Network Management"
    ]
  };
  
  return (
    <div className="min-h-screen bg-background overflow-x-hidden">
      {/* PageLoader removed — blocks LCP; prefer skeleton/Suspense fallbacks */}
      
      <SEOHead 
        title={t.hero.title}
        description={t.hero.description}
        canonical="/"
        type="website"
        schema={organizationSchema}
      />
      
      <Analytics gaId="G-XXXXXXXXXX" />
      <FacebookPixel pixelId="PIXEL_ID" />
      
      {/* Above the fold — eagerly loaded */}
      <Header />
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[60] focus:rounded-md focus:bg-background focus:px-4 focus:py-3 focus:text-foreground focus:shadow-lg"
      >
        {t.accessibility.skipToContent}
      </a>
      <main id="main-content">
        <Hero />
        <AnimatedSection direction="up" duration="slow">
          <TrustBar />
        </AnimatedSection>

        {/* Below the fold — lazy-loaded */}
        <Suspense fallback={<Blank />}>
          <DeferredSection>
            <AnimatedSection delay={100} direction="up">
              <HomeTrustProof />
            </AnimatedSection>
          </DeferredSection>
          <DeferredSection>
            <section className="bg-slate-950 pb-16 sm:pb-20 lg:pb-24">
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <AvailabilityProofPanel />
              </div>
            </section>
          </DeferredSection>
          <DeferredSection>
            <AnimatedSection delay={100} direction="left">
              <WhyConsultic />
            </AnimatedSection>
          </DeferredSection>
          <DeferredSection>
            <SiteExplorer />
          </DeferredSection>
          <DeferredSection>
            <AnimatedSection delay={100} direction="right" parallax parallaxSpeed={0.06}>
              <Services />
            </AnimatedSection>
          </DeferredSection>
          <DeferredSection>
            <AnimatedSection delay={100} direction="scale" duration="slow">
              <About />
            </AnimatedSection>
          </DeferredSection>
          <DeferredSection>
            <AnimatedSection delay={100} direction="up">
              <Testimonials />
            </AnimatedSection>
          </DeferredSection>
          <DeferredSection>
            <AnimatedSection delay={100} direction="up">
              <GoogleReviews />
            </AnimatedSection>
          </DeferredSection>
          <DeferredSection>
            <AnimatedSection delay={100} direction="up">
              <ClientsMarquee />
            </AnimatedSection>
          </DeferredSection>
          <DeferredSection>
            <AnimatedSection delay={100} direction="left" parallax parallaxSpeed={0.05}>
              <News />
            </AnimatedSection>
          </DeferredSection>
          <DeferredSection>
            <AnimatedSection delay={100} direction="up">
              <QuoteForm />
            </AnimatedSection>
          </DeferredSection>
          <DeferredSection>
            <AnimatedSection delay={100} direction="up">
              <Contact />
            </AnimatedSection>
          </DeferredSection>
        </Suspense>
      </main>
      <DeferredSection>
        <Suspense fallback={<Blank />}>
          <Footer />
        </Suspense>
      </DeferredSection>
    </div>
  );
};

export default Index;
